import { FC, ReactNode, useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';

export type GiftSellerPageTab = {
  key: string;
  label: string;
  element: ReactNode;
};

/**
 * 독립 페이지 여러 개를 상단 탭 하나로 묶는 얇은 껍데기 (사이드바 메뉴 수 축소용).
 * - 탭 전환은 ?tab= (첫 탭은 쿼리 없음, 북마크 보존) — 전화주문 관리와 같은 방식
 * - 각 탭의 페이지는 자기 헤더(제목·등록 버튼·통계)를 그대로 가지므로
 *   여기서는 제목 없이 탭 줄만 얹는다 (제목 중복 방지)
 */
export const GiftSellerTabbedPage: FC<{ tabs: GiftSellerPageTab[] }> = function ({
  tabs,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const first = tabs[0];
  const rawTab = searchParams.get('tab');
  const current = tabs.find((t) => t.key === rawTab) ?? first;

  // tab 외 쿼리(?date= 등 딥링크)는 건드리지 않는다
  const handleTabChange = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === first?.key) params.delete('tab');
          else params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, first?.key],
  );

  if (!current) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex gap-1 border-b border-gray-200 bg-white px-6 pt-2">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => handleTabChange(item.key)}
            className={
              current.key === item.key
                ? 'border-b-2 border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-600'
                : 'border-b-2 border-transparent px-4 py-2 text-sm text-gray-500 hover:text-gray-700'
            }
          >
            {item.label}
          </button>
        ))}
      </div>
      {/* 탭별 페이지는 key로 완전히 갈아끼움 (탭 간 상태 섞임 방지) */}
      <div key={current.key}>{current.element}</div>
    </div>
  );
};
